import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type {
  Actuacion,
  Cliente,
  Documento,
  EstadoExpediente,
  EventoAgenda,
  Expediente,
  Honorario,
  MovimientoEconomico,
  Parte,
  Rendicion,
} from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { Boton } from "../../components/Boton";
import { Cargando } from "../../components/Cargando";
import { ConfirmacionDialog } from "../../components/ConfirmacionDialog";
import { EncabezadoPagina } from "../../components/EncabezadoPagina";
import { EstadoVacio } from "../../components/EstadoVacio";
import { Insignia } from "../../components/Insignia";
import { Select } from "../../components/Formulario";
import { Tarjeta } from "../../components/Tarjeta";
import { useToast } from "../../components/Toast";
import { useEstadoEnUrl } from "../../hooks/useEstadoEnUrl";
import { api, ErrorApi } from "../../lib/apiClient";
import { resolverColorSemantico } from "../../lib/colorSemantico";
import { FormularioExpediente } from "./FormularioExpediente";
import { PestanaActuaciones } from "./PestanaActuaciones";
import { PestanaAgenda } from "./PestanaAgenda";
import { PestanaDocumentos } from "./PestanaDocumentos";
import { PestanaEconomico } from "./PestanaEconomico";
import { PestanaEvaluacion } from "./PestanaEvaluacion";
import { PestanaHistorial } from "./PestanaHistorial";
import { PestanaPartes } from "./PestanaPartes";
import { PestanaProceso } from "./PestanaProceso";

// Ítem mínimo del checklist de la etapa actual, sólo para derivar "checklist
// pendiente más urgente" en el resumen operativo — reutiliza el mismo
// endpoint que ya consume PestanaProceso (GET /:id/etapa/checklist), sin
// duplicar su lógica de edición.
interface ChecklistItemResumen {
  texto: string;
  obligatorio: boolean;
  estado: string | null;
  completado: boolean;
}

function fechaMasReciente(fechas: string[]): string | null {
  if (fechas.length === 0) return null;
  return fechas.reduce((max, f) => (f > max ? f : max));
}

function diasEntre(desde: string): number {
  const ms = Date.now() - new Date(desde).getTime();
  return Math.max(Math.round(ms / 86400000), 0);
}

interface Ficha {
  expediente: Expediente;
  cliente: Cliente;
  partes: Parte[];
  actuaciones: Actuacion[];
  eventosAgenda: EventoAgenda[];
  documentos: Documento[];
  movimientos: MovimientoEconomico[];
  honorarios: (Honorario & { cobrado: number; saldoPendiente: number | null })[];
  rendiciones: Rendicion[];
}

const ESTADOS: EstadoExpediente[] = [
  "Consulta Inicial",
  "En Trámite",
  "Con Resolución",
  "En Ejecución o Cobro",
  "Archivado",
  "Desistido",
];

// Estados con consecuencias (cierran el expediente) — se identifican en la
// propia opción del Select, sin teñir todo el control de rojo, y disparan
// el diálogo de confirmación con estilo de peligro (ver ejecutarCambioEstado).
const ESTADOS_SENSIBLES = new Set<EstadoExpediente>(["Archivado", "Desistido"]);

const PESTANAS = [
  { clave: "datos", etiqueta: "Datos" },
  { clave: "partes", etiqueta: "Partes" },
  { clave: "actuaciones", etiqueta: "Actuaciones" },
  { clave: "agenda", etiqueta: "Agenda" },
  { clave: "documentos", etiqueta: "Documentos" },
  { clave: "economico", etiqueta: "Económico" },
  { clave: "evaluacion", etiqueta: "Evaluación y estrategia" },
  { clave: "proceso", etiqueta: "Proceso" },
  { clave: "historial", etiqueta: "Historial" },
] as const;

export function ExpedienteFichaPage() {
  const { id } = useParams<{ id: string }>();
  const { mostrar } = useToast();
  const { permisos } = useAuth();
  const veEvaluacion = permisos.has("expedientes.ver_evaluacion");
  const veProcesos = permisos.has("procesos.ver");
  const veAuditoria = permisos.has("auditoria.ver");
  const pestanasVisibles = PESTANAS.filter((p) => p.clave !== "evaluacion" || veEvaluacion)
    .filter((p) => p.clave !== "proceso" || veProcesos)
    .filter((p) => p.clave !== "historial" || veAuditoria);
  const [ficha, setFicha] = useState<Ficha | null>(null);
  const [error, setError] = useState<string | null>(null);
  // La pestaña activa vive en ?tab= (useEstadoEnUrl) para sobrevivir a un
  // "volver" y al botón Atrás del navegador — antes se reseteaba siempre a
  // "datos" al volver a entrar a la ficha.
  const [pestanaUrl, setPestanaUrl] = useEstadoEnUrl("tab", "datos");
  const pestana = (PESTANAS.some((p) => p.clave === pestanaUrl) ? pestanaUrl : "datos") as (typeof PESTANAS)[number]["clave"];
  const setPestana = setPestanaUrl;
  const [modalEditar, setModalEditar] = useState(false);
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const [confirmarArchivar, setConfirmarArchivar] =
    useState<EstadoExpediente | null>(null);
  const [cambiandoEstado, setCambiandoEstado] = useState(false);
  // Sólo para el resumen operativo (visible sin entrar a la pestaña
  // Proceso) — null mientras no aplica o no cargó todavía.
  const [checklistResumen, setChecklistResumen] = useState<ChecklistItemResumen[] | null>(null);

  const cargar = useCallback(() => {
    if (!id) return;
    api
      .get<Ficha>(`/expedientes/${id}`)
      .then(setFicha)
      .catch((err) =>
        setError(err instanceof ErrorApi ? err.message : "No se pudo cargar el expediente."),
      );
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Checklist de la etapa actual, sólo para el resumen operativo — mismo
  // endpoint que ya usa PestanaProceso (GET /:id/etapa/checklist), gateado
  // por el mismo permiso procesos.ver. Sin proceso asignado no hay nada
  // que pedir.
  useEffect(() => {
    const procesoId = ficha?.expediente.proceso_id;
    if (!id || !procesoId || !veProcesos) {
      setChecklistResumen(null);
      return;
    }
    api
      .get<{ items: ChecklistItemResumen[] }>(`/expedientes/${id}/etapa/checklist`)
      .then((d) => setChecklistResumen(d.items))
      .catch(() => setChecklistResumen(null));
  }, [id, ficha?.expediente.proceso_id, ficha?.expediente.etapa_actual_id, veProcesos]);

  // Estado vacío/error nunca es un callejón sin salida: siempre ofrece una
  // ruta segura de vuelta (expediente inexistente, sin permisos, error de
  // red, etc.).
  if (error) {
    return (
      <EstadoVacio
        titulo="No se pudo abrir el expediente"
        descripcion={error}
        accion={
          <Link to="/expedientes" className="text-sm font-medium text-secundario hover:underline">
            Volver a Expedientes
          </Link>
        }
      />
    );
  }
  if (!ficha) return <Cargando />;

  const { expediente, cliente } = ficha;

  // Resumen operativo: deriva todo de datos ya cargados en `ficha` (más el
  // checklist, cargado aparte sólo cuando aplica) — cero fetches nuevos por
  // fuera de lo que PestanaProceso ya pide, sólo se levanta un nivel para
  // que se vea sin entrar a ninguna pestaña.
  const eventosPendientesOAtrasados = ficha.eventosAgenda
    .filter((e) => e.estado === "Pendiente" || e.estado === "Vencido")
    .sort((a, b) => (a.fecha_vencimiento < b.fecha_vencimiento ? -1 : 1));
  const proximoVencimiento = eventosPendientesOAtrasados[0] ?? null;
  const checklistPendiente =
    checklistResumen?.find(
      (c) => c.obligatorio && !(c.estado === "Recibido" || c.estado === "Validado" || (c.estado == null && c.completado)),
    ) ?? null;
  const proximaAccion = checklistPendiente?.texto ?? proximoVencimiento?.titulo ?? null;
  const ultimaActuacion = fechaMasReciente(ficha.actuaciones.map((a) => a.fecha));
  const diasSinMovimiento = diasEntre(ultimaActuacion ?? expediente.creado_en);

  async function guardarEdicion(valores: Record<string, unknown>) {
    setGuardandoEdicion(true);
    try {
      await api.put(`/expedientes/${expediente.id}`, valores);
      mostrar("Expediente actualizado.", "exito");
      setModalEditar(false);
      cargar();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi ? err.message : "No se pudo actualizar.",
        "error",
      );
    } finally {
      setGuardandoEdicion(false);
    }
  }

  async function cambiarEstado(nuevoEstado: EstadoExpediente) {
    if (nuevoEstado === "Archivado" || nuevoEstado === "Desistido") {
      setConfirmarArchivar(nuevoEstado);
      return;
    }
    await ejecutarCambioEstado(nuevoEstado);
  }

  async function ejecutarCambioEstado(nuevoEstado: EstadoExpediente) {
    setCambiandoEstado(true);
    try {
      await api.patch(`/expedientes/${expediente.id}/estado`, {
        estado: nuevoEstado,
      });
      mostrar(`Estado actualizado a "${nuevoEstado}".`, "exito");
      setConfirmarArchivar(null);
      cargar();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado.",
        "error",
      );
    } finally {
      setCambiandoEstado(false);
    }
  }

  return (
    <div>
      <EncabezadoPagina
        titulo={expediente.numero_expediente}
        descripcion={`${expediente.materia_nombre} · ${expediente.submateria_nombre} · Cliente: ${cliente.nombre_completo}`}
        volver={{ rutaPadre: "/expedientes", etiqueta: "Volver a Expedientes" }}
        migas={[
          { etiqueta: "Expedientes", ruta: "/expedientes" },
          { etiqueta: expediente.numero_expediente },
        ]}
        acciones={
          <>
            <Boton variante="secundario" onClick={() => setModalEditar(true)}>
              Editar
            </Boton>
            <Select
              value={expediente.estado}
              onChange={(e) =>
                cambiarEstado(e.target.value as EstadoExpediente)
              }
              disabled={cambiandoEstado}
              className="w-52"
            >
              {ESTADOS.map((e) => (
                <option key={e} value={e}>
                  {/* El sufijo "(requiere confirmación)" es una advertencia
                      ANTES de elegir ese estado — una vez que el expediente
                      ya está en ese estado, el valor mostrado (cerrado, sin
                      desplegar) es exactamente este texto de la opción
                      seleccionada, así que no repetimos la advertencia sobre
                      un cambio que ya ocurrió. */}
                  {e}
                  {ESTADOS_SENSIBLES.has(e) && e !== expediente.estado ? " (requiere confirmación)" : ""}
                </option>
              ))}
            </Select>
          </>
        }
      />

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Insignia texto={expediente.estado} />
        <Insignia texto={expediente.prioridad} tipo="prioridad" />
        {veEvaluacion && expediente.estado_procesal_nombre && (
          <Insignia texto={expediente.estado_procesal_nombre} colorSemantico={expediente.estado_procesal_color} />
        )}
        {veEvaluacion && expediente.nivel_riesgo && (
          <Insignia texto={`Riesgo: ${expediente.nivel_riesgo}`} tipo="riesgo" claveColor={expediente.nivel_riesgo} />
        )}
        {veEvaluacion &&
          expediente.probabilidad_exito &&
          expediente.probabilidad_exito !== "No evaluada" && (
            <Insignia
              texto={`Probabilidad: ${expediente.probabilidad_exito}`}
              tipo="probabilidad"
              claveColor={expediente.probabilidad_exito}
            />
          )}
        <Link to="/clientes" className="text-xs text-texto-secundario transition-colors hover:text-secundario hover:underline">
          Ver Clientes
        </Link>
      </div>

      <Tarjeta className="mb-6 grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 lg:grid-cols-6">
        <Dato
          etiqueta="Etapa actual"
          valor={expediente.proceso_id ? (expediente.etapa_nombre ?? "Sin etapa asignada") : "Sin proceso asignado"}
        />
        <Dato
          etiqueta="Responsable actual"
          valor={expediente.proceso_id ? (expediente.etapa_responsable_nombre ?? "Sin responsable") : "Sin proceso asignado"}
        />
        <Dato etiqueta="Próxima acción" valor={proximaAccion ?? "Sin acciones pendientes"} />
        <Dato
          etiqueta="Próximo vencimiento"
          valor={
            proximoVencimiento
              ? `${proximoVencimiento.fecha_vencimiento} — ${proximoVencimiento.titulo}${
                  proximoVencimiento.estado === "Vencido" ? " (vencido)" : ""
                }`
              : "Sin vencimientos pendientes"
          }
          alerta={proximoVencimiento?.estado === "Vencido"}
        />
        <Dato
          etiqueta="Checklist pendiente"
          valor={
            !expediente.proceso_id
              ? "Sin proceso asignado"
              : checklistResumen == null
                ? "Cargando..."
                : (checklistPendiente?.texto ?? "Sin pendientes")
          }
        />
        <Dato etiqueta="Días sin movimiento" valor={`${diasSinMovimiento} días`} alerta={diasSinMovimiento >= 30} />
      </Tarjeta>

      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-borde">
        {pestanasVisibles.map((p) => (
          <button
            key={p.clave}
            onClick={() => setPestana(p.clave)}
            className={`shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secundario focus-visible:ring-offset-2 ${
              pestana === p.clave
                ? "border-principal text-principal"
                : "border-transparent text-texto-secundario hover:text-texto"
            }`}
          >
            {p.etiqueta}
          </button>
        ))}
      </div>

      {pestana === "datos" && (
        <Tarjeta className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <Dato etiqueta="Materia" valor={expediente.materia_nombre ?? "—"} color={expediente.materia_color} />
          <Dato
            etiqueta="Submateria"
            valor={expediente.submateria_nombre ?? "—"}
            color={expediente.materia_color}
          />
          <Dato etiqueta="Fuero" valor={expediente.fuero_nombre ?? "—"} color={expediente.fuero_color} />
          <Dato
            etiqueta="Organismo / Tribunal"
            valor={expediente.organismo_nombre ?? "—"}
            color={expediente.fuero_color}
          />
          <Dato
            etiqueta="Secretaría / Sala"
            valor={expediente.secretaria_nombre ?? "—"}
          />
          <Dato
            etiqueta="Rol del cliente"
            valor={expediente.rol_cliente ?? "—"}
          />
          <Dato
            etiqueta="Tipo de reclamo"
            valor={expediente.tipo_reclamo ?? "—"}
          />
          <Dato
            etiqueta="Fecha de apertura"
            valor={expediente.fecha_apertura}
          />
          <Dato
            etiqueta="Fecha de cierre"
            valor={expediente.fecha_cierre ?? "—"}
          />
          <Dato
            etiqueta="Monto reclamado estimado"
            valor={
              expediente.monto_reclamado_estimado != null
                ? `$${expediente.monto_reclamado_estimado.toLocaleString("es-AR")}`
                : "—"
            }
          />
          <Dato etiqueta="Cliente" valor={cliente.nombre_completo} />
          <div className="sm:col-span-2">
            <Dato
              etiqueta="Observaciones"
              valor={expediente.observaciones ?? "—"}
            />
          </div>
        </Tarjeta>
      )}

      {pestana === "partes" && (
        <PestanaPartes
          expedienteId={expediente.id}
          partes={ficha.partes}
          onCambio={cargar}
        />
      )}
      {pestana === "actuaciones" && (
        <PestanaActuaciones
          expedienteId={expediente.id}
          actuaciones={ficha.actuaciones}
          partes={ficha.partes}
          documentos={ficha.documentos}
          onCambio={cargar}
        />
      )}
      {pestana === "agenda" && (
        <PestanaAgenda
          expedienteId={expediente.id}
          eventos={ficha.eventosAgenda}
          onCambio={cargar}
        />
      )}
      {pestana === "documentos" && (
        <PestanaDocumentos
          expedienteId={expediente.id}
          documentos={ficha.documentos}
          onCambio={cargar}
        />
      )}
      {pestana === "economico" && (
        <PestanaEconomico
          expedienteId={expediente.id}
          clienteId={cliente.id}
          honorarios={ficha.honorarios}
          rendiciones={ficha.rendiciones}
          movimientos={ficha.movimientos}
          onCambio={cargar}
        />
      )}
      {pestana === "evaluacion" && veEvaluacion && (
        <PestanaEvaluacion expediente={expediente} onCambio={cargar} />
      )}
      {pestana === "proceso" && veProcesos && (
        <PestanaProceso
          expediente={expediente}
          actuaciones={ficha.actuaciones}
          eventosAgenda={ficha.eventosAgenda}
          documentos={ficha.documentos}
          movimientos={ficha.movimientos}
          onCambio={cargar}
        />
      )}
      {pestana === "historial" && veAuditoria && (
        <PestanaHistorial expedienteId={expediente.id} />
      )}

      <FormularioExpediente
        abierto={modalEditar}
        clientes={[cliente]}
        guardando={guardandoEdicion}
        valoresIniciales={expediente}
        onCerrar={() => setModalEditar(false)}
        onGuardar={guardarEdicion}
      />

      <ConfirmacionDialog
        abierto={confirmarArchivar !== null}
        titulo={`Cambiar estado a "${confirmarArchivar}"`}
        mensaje="Esta acción marca el expediente como cerrado. Podés volver a cambiar el estado luego si es necesario."
        cargando={cambiandoEstado}
        peligroso
        onConfirmar={() =>
          confirmarArchivar && ejecutarCambioEstado(confirmarArchivar)
        }
        onCancelar={() => setConfirmarArchivar(null)}
        textoConfirmar="Sí, cambiar"
      />
    </div>
  );
}

function Dato({
  etiqueta,
  valor,
  color,
  alerta,
}: {
  etiqueta: string;
  valor: string;
  color?: string | null;
  alerta?: boolean;
}) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-texto-secundario">{etiqueta}</p>
      <p className={`mt-0.5 flex items-center gap-1.5 text-sm ${alerta ? "font-medium text-error" : "text-texto"}`}>
        {color !== undefined && <span className={`h-2 w-2 flex-shrink-0 rounded-full ${resolverColorSemantico(color).punto}`} />}
        {valor}
      </p>
    </div>
  );
}
